import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import DEFAULT_OPERATIONS from "../Cart.gql";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";

export const useProduct = (props = {}) => {
  const { item, setIsCartUpdating } = props;

  const { removeItemMutation, updateItemQuantityMutation } = DEFAULT_OPERATIONS;

  const { addToast } = useToasts();

  const flatProduct = flattenProduct(item);

  const [
    removeItemFromCart,
    {
      called: removeItemCalled,
      error: removeItemError,
      loading: removeItemLoading,
    },
  ] = useMutation(removeItemMutation, {
    onCompleted: () => {
      addToast("Item successfully removed from Cart.", {
        appearance: "success",
        autoDismiss: true,
        style: {
          backgroundColor: "#027B51" /* Custom background color */,
          color: "#fff" /* Custom text color */,
        },
      });
    },
  });

  const [
    updateItemQuantity,
    {
      loading: updateItemLoading,
      error: updateError,
      called: updateItemCalled,
    },
  ] = useMutation(updateItemQuantityMutation);

  const { cartId } = useSelector((state) => state.cartSlice);

  const isProductUpdating = useMemo(() => {
    if (updateItemCalled || removeItemCalled) {
      return removeItemLoading || updateItemLoading;
    } else {
      return false;
    }
  }, [
    updateItemCalled,
    removeItemCalled,
    removeItemLoading,
    updateItemLoading,
  ]);

  const handleRemoveFromCart = useCallback(async () => {
    try {
      await removeItemFromCart({
        variables: {
          cartId,
          itemId: item.uid,
        },
      });
    } catch (err) {}
  }, [cartId, item.uid, removeItemFromCart]);

  const handleUpdateItemQuantity = useCallback(
    async (quantity) => {
      try {
        await updateItemQuantity({
          variables: {
            cartId,
            itemId: item.uid,
            quantity,
          },
        });
      } catch (err) {}
    },
    [cartId, item.uid, updateItemQuantity]
  );

  useEffect(() => {
    setIsCartUpdating(isProductUpdating);
    return () => setIsCartUpdating(false);
  }, [setIsCartUpdating, isProductUpdating]);

  return {
    handleRemoveFromCart,
    handleUpdateItemQuantity,
    isProductUpdating,
    updateError,
    removeItemError,
    product: flatProduct,
  };
};
const flattenProduct = (item) => {
  const {
    configurable_options: options = [],
    prices,
    product,
    quantity,
  } = item;

  const { price } = prices;
  const { value: unitPrice, currency } = price;

  const {
    name,
    small_image,
    stock_status: stockStatus,
    url_key: urlKey,
    sku,
  } = product;

  let value = {};
  if (item["__typename"].toLowerCase() === "bundlecartitem") {
    const chosenBundleOptionUid = item.bundle_options[0].uid;
    const foundChosenBundle = item.product.items.find(
      (item) => item.uid === chosenBundleOptionUid
    );
    if (foundChosenBundle) {
      const totalPrice = foundChosenBundle.options.reduce((acc, curr) => {
        return (
          acc +
          curr.product.price_range.maximum_price.regular_price.value *
            curr.quantity
        );
      }, 0);

      const afterDiscount = foundChosenBundle.options.reduce((acc, curr) => {
        return (
          acc +
          curr.product.price_range.maximum_price.final_price.value *
            curr.quantity
        );
      }, 0);

      value = {
        regularPrice: { value: totalPrice },
        discountPrice: { value: afterDiscount },
      };
    } else {
      const regularPrice = product.price_range.maximum_price.regular_price;
      const discountPrice =
        product.price_range.maximum_price.discount &&
        !!product.price_range.maximum_price.discount.amount_off &&
        product.price_range.maximum_price.final_price;
      value = {
        regularPrice,
        discountPrice,
      };
    }
  } else if (item["__typename"].toLowerCase() === "configurablecartitem") {
    const { configured_variant } = item;
    const regularPrice =
      configured_variant.price_range.minimum_price.regular_price;
    const discountPrice =
      configured_variant.price_range.minimum_price.discount &&
      !!configured_variant.price_range.minimum_price.discount.amount_off &&
      configured_variant.price_range.minimum_price.final_price;
    const savedPrice =
      configured_variant.price_range.minimum_price.discount &&
      !!configured_variant.price_range.minimum_price.discount.amount_off &&
      configured_variant.price_range.minimum_price.discount;
    value = {
      regularPrice,
      discountPrice,
      savedPrice,
    };
  } else {
    const regularPrice = product.price_range.minimum_price.regular_price;
    const discountPrice =
      product.price_range.minimum_price.discount &&
      !!product.price_range.minimum_price.discount.amount_off &&
      product.price_range.minimum_price.final_price;
    const savedPrice =
      product.price_range.minimum_price.discount &&
      !!product.price_range.minimum_price.discount.amount_off &&
      product.price_range.minimum_price.discount;
    value = {
      regularPrice,
      discountPrice,
      savedPrice,
    };
  }
  const { discountPrice, regularPrice, savedPrice } = value;

  const PriceDetails = {
    regularPrice,
    discountPrice,
    savedPrice,
  };

  const { url: image } = small_image;

  return {
    currency,
    image,
    name,
    options,
    quantity,
    stockStatus,
    unitPrice,
    urlKey,
    PriceDetails,
    sku,
  };
};
